import React, { useState, useEffect } from 'react';
import { Button, DataTable, Toast, UserFilters, ConfirmModal } from '../../../shared/components';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { useUserManagement, useConfirm } from '../../../shared/hooks';
import { RoleProtectedRoute } from '../../../shared/components';
import UserFormModal from './UserFormModal';

/**
 * Lista de usuarios con filtros avanzados
 * Permite filtrar por username, rol y estado
 */
const UserList = () => {
  const {
    users,
    loading,
    error,
    filters,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    applyUserFilters,
    applyFiltersNow,
    clearFilters,
    clearError,
  } = useUserManagement();

  // Hook para confirmaciones
  const { confirmState, confirm, closeConfirm } = useConfirm();

  // Estados de modales
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Estados de notificaciones
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success'
  });

  // Cargar usuarios al montar el componente
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Mostrar notificación
  const showToast = (message, type = 'success') => {
    setToast({
      isVisible: true,
      message,
      type
    });
  };

  // Ocultar notificación
  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // Manejar búsqueda y filtros
  const handleSearch = async () => {
    // La búsqueda ahora se maneja dentro de UserFilters
    // Solo necesitamos aplicar los filtros locales
    applyFiltersNow();
  };

  // Manejar cambio de filtros (solo actualizar estado, no aplicar)
  const handleFiltersChange = (newFilters) => {
    applyUserFilters(newFilters);
  };

  // Manejar limpieza de filtros
  const handleClearFilters = () => {
    clearFilters();
    fetchUsers();
  };

  // Manejar eliminación de usuario
  const handleDeleteUser = async (user) => {
    const confirmed = await confirm({
      title: 'Confirmar Eliminación',
      message: `¿Estás seguro de que quieres eliminar al usuario "${user.username}"? Esta acción no se puede deshacer.`,
      type: 'danger',
      confirmText: 'Eliminar Usuario',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        await deleteUser(user.id);
        showToast(`Usuario "${user.username}" eliminado exitosamente`, 'success');
      }
    });
    
    if (!confirmed) {
      showToast('Operación cancelada', 'warning');
    }
  };

  // Manejar edición de usuario
  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  // Manejar éxito de operaciones
  const handleSuccess = (user, operation) => {
    const messages = {
      create: `Usuario "${user.username}" creado exitosamente`,
      edit: `Usuario "${user.username}" actualizado exitosamente`
    };
    
    showToast(messages[operation], 'success');
  };

  // Configuración de columnas de la tabla
  const columns = [
    {
      key: 'username',
      label: 'Username',
      render: (user) => (
        <div className="font-medium text-gray-900">
          {user.username}
        </div>
      )
    },
    {
      key: 'name',
      label: 'Nombre',
      render: (user) => (
        <div className="text-gray-600">
          {user.name}
        </div>
      )
    },
    {
      key: 'lastName',
      label: 'Apellido',
      render: (user) => (
        <div className="text-gray-600">
          {user.lastName}
        </div>
      )
    },
    {
      key: 'role',
      label: 'Rol',
      render: (user) => {
        const roleColors = {
          admin: 'bg-purple-100 text-purple-800',
          user: 'bg-blue-100 text-blue-800'
        };
        
        const roleLabels = {
          admin: 'Administrador',
          user: 'Usuario'
        };

        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${roleColors[user.role] || 'bg-gray-100 text-gray-800'}`}>
            {roleLabels[user.role] || user.role}
          </span>
        );
      }
    },
    {
      key: 'isActive',
      label: 'Estado',
      render: (user) => {
        const isActive = user.isActive;
        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            isActive 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {isActive ? 'Activo' : 'Inactivo'}
          </span>
        );
      }
    },
    {
      key: 'createdAt',
      label: 'Creado',
      render: (user) => (
        <div className="text-gray-600 text-sm">
          {new Date(user.createdAt).toLocaleDateString('es-ES')}
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (user) => (
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEditUser(user)}
            className="text-green-600 hover:text-green-800"
            title="Editar usuario"
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteUser(user)}
            className="text-red-600 hover:text-red-800"
            title="Eliminar usuario"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 mb-4">
          <p className="mb-4">{error}</p>
          <Button onClick={clearError} variant="outline">
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <RoleProtectedRoute requiredRole="admin">
      <div className="space-y-6">
        {/* Header con botón de nuevo usuario */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Gestión de Usuarios
            </h2>
            <p className="text-gray-600 mt-1">
              Administra usuarios del sistema, roles y permisos
            </p>
          </div>
          <Button 
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Usuario
          </Button>
        </div>

        {/* Filtros de usuarios */}
        <UserFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          onSearch={handleSearch}
          loading={loading}
        />

        {/* Tabla de usuarios */}
        <div className="bg-white rounded-lg shadow">
          <DataTable
            section="Usuarios"
            data={users}
            columns={columns}
            loading={loading}
            error={error}
            onRetry={fetchUsers}
            emptyMessage="No hay usuarios que coincidan con los filtros aplicados"
            showSearch={false}
            showFilters={false}
          />
        </div>

        {/* Modal de creación */}
        <UserFormModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          mode="create"
          onSuccess={handleSuccess}
          createUser={createUser}
        />

        {/* Modal de edición */}
        <UserFormModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          mode="edit"
          onSuccess={handleSuccess}
          updateUser={updateUser}
        />

        {/* Modal de confirmación */}
        <ConfirmModal
          isOpen={confirmState.isOpen}
          onClose={closeConfirm}
          onConfirm={confirmState.onConfirm}
          title={confirmState.title}
          message={confirmState.message}
          type={confirmState.type}
          confirmText={confirmState.confirmText}
          cancelText={confirmState.cancelText}
          loading={confirmState.loading}
        />

        {/* Notificación Toast */}
        <Toast
          isVisible={toast.isVisible}
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
          duration={4000}
        />
      </div>
    </RoleProtectedRoute>
  );
};

export default UserList; 